import { useTranslation } from 'react-i18next'
import { UtensilsCrossed, Coffee, Pizza, Instagram } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { type RouteKey } from '@/i18n/routes'
import { cn } from '@/lib/utils'

const quickLinks: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'maia', labelKey: 'nav.maia' },
  { key: 'bar', labelKey: 'nav.bar' },
  { key: 'pizza', labelKey: 'nav.pizza' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

const addressLinks: {
  key: 'maia' | 'bar' | 'pizza'
  icon: typeof UtensilsCrossed
  labelKey: string
  href: string
}[] = [
  {
    key: 'maia',
    icon: UtensilsCrossed,
    labelKey: 'home:venues.maia.shortAddress',
    href: 'https://maps.app.goo.gl/ZRceKAtxCSbXj4Y27',
  },
  {
    key: 'bar',
    icon: Coffee,
    labelKey: 'home:venues.bar.shortAddress',
    href: 'https://maps.app.goo.gl/PTELeydb72gr9DnK8',
  },
  {
    key: 'pizza',
    icon: Pizza,
    labelKey: 'home:venues.pizza.shortAddress',
    href: 'https://maps.app.goo.gl/caAbFie3hvwb73eR6',
  },
]

interface FooterProps {
  variant?: 'maia' | 'bar' | 'pizza'
}

export function Footer({ variant = 'maia' }: FooterProps) {
  const { t } = useTranslation(['common', 'home'])

  const isBarLight = variant === 'bar'

  const brandBg = isBarLight
    ? 'bg-bar-dark'
    : variant === 'pizza'
      ? 'bg-pizza-dark'
      : 'bg-maia-dark'
  const textColor = isBarLight ? 'text-ink' : 'text-white'
  const mutedColor = isBarLight ? 'text-ink/70' : 'text-white/70'
  const bodyColor = isBarLight ? 'text-ink/80' : 'text-white/80'
  const subtleColor = isBarLight ? 'text-ink/60' : 'text-white/60'
  const borderColor = isBarLight ? 'border-ink/10' : 'border-white/10'
  const linkColor = isBarLight
    ? 'text-ink hover:text-ink/70'
    : variant === 'pizza'
      ? 'text-pizza-light hover:text-white'
      : 'text-maia-light hover:text-white'

  return (
    <footer className={cn(textColor, brandBg)}>
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="space-y-4">
            <span className="text-2xl font-extrabold tracking-tight">Maia e Tas</span>
            <p className={bodyColor}>{t('footer.tagline')}</p>
          </div>

          {/* Addresses */}
          <div className="space-y-4">
            <h3 className={cn('text-sm font-semibold', mutedColor)}>
              {t('footer.addresses')}
            </h3>
            <ul className="space-y-3 text-sm">
              {addressLinks.map(({ key, icon: Icon, labelKey, href }) => (
                <li key={key}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'group inline-flex items-start gap-2 transition-colors',
                      linkColor
                    )}
                  >
                    <Icon
                      className="mt-0.5 h-4 w-4 shrink-0"
                      strokeWidth={1}
                      aria-hidden="true"
                    />
                    <span>{t(labelKey)}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div className="space-y-4">
            <h3 className={cn('text-sm font-semibold', mutedColor)}>
              {t('footer.navigation')}
            </h3>
            <ul className="space-y-2 text-sm">
              {quickLinks.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className={cn('transition-colors', linkColor)}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div className="space-y-4">
            <h3 className={cn('text-sm font-semibold', mutedColor)}>
              {t('footer.social')}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://www.instagram.com/maiaetas_crema/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn('inline-flex items-center gap-2 transition-colors', linkColor)}
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                  maiaetas_crema
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/baretas_crema/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn('inline-flex items-center gap-2 transition-colors', linkColor)}
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                  baretas_crema
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/pizzaetas_crema/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn('inline-flex items-center gap-2 transition-colors', linkColor)}
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                  pizzaetas_crema
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className={cn('mt-12 flex flex-col items-center justify-between gap-4 border-t pt-8 text-sm sm:flex-row', borderColor, subtleColor)}>
          <div className="flex flex-col items-center gap-1 sm:items-start">
            <p>© {new Date().getFullYear()} Maia e Tas. {t('footer.allRightsReserved')}</p>
            <p>
              Sviluppato da{' '}
              <a
                href="https://novaui.it/"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('transition-colors', linkColor)}
              >
                NovaUI
              </a>
            </p>
          </div>
          <div className="flex gap-4">
            <LocalizedLink to="privacy" className={cn('transition-colors', linkColor)}>
              {t('footer.privacy')}
            </LocalizedLink>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openCookiePreferences'))}
              className={cn('transition-colors', linkColor)}
            >
              {t('footer.cookiePolicy')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
